import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ShieldCheck, BadgeCheck, Clock, Upload, X, AlertCircle,
  CheckCircle, Camera, FileText, Loader2, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

const GuideVerificationCard = ({ user, onVerificationRequested }) => {
  const { i18n } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [documentType, setDocumentType] = useState('id_card');
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const isVerified = user?.verified;
  const isPending = user?.verification_requested && !isVerified;
  const isRejected = user?.verification_rejected;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(i18n.language === 'fr' ? 'Fichier trop volumineux (max 10MB)' : 'File too large (max 10MB)');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocumentPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitVerification = async () => {
    if (!documentPreview) {
      toast.error(i18n.language === 'fr' ? 'Veuillez ajouter un document' : 'Please add a document');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/guides/request-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          document_type: documentType,
          document_url: documentPreview
        })
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) throw new Error('Failed');

      toast.success(i18n.language === 'fr' 
        ? 'Demande de vérification envoyée ! Nous vous répondrons sous 24-48h.'
        : 'Verification request sent! We\'ll respond within 24-48h.');
      
      setShowModal(false);
      setDocumentPreview(null);
      onVerificationRequested?.();
    } catch (error) {
      toast.error(i18n.language === 'fr' ? 'Erreur lors de l\'envoi' : 'Error sending request');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const documentTypes = [
    { id: 'id_card', label: i18n.language === 'fr' ? 'Carte d\'identité' : 'ID Card', icon: FileText },
    { id: 'passport', label: i18n.language === 'fr' ? 'Passeport' : 'Passport', icon: FileText },
    { id: 'driving_license', label: i18n.language === 'fr' ? 'Permis de conduire' : 'Driving License', icon: FileText },
  ];

  return (
    <>
      <div
        className={`rounded-2xl border-2 overflow-hidden ${
          isVerified 
            ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' 
            : isPending
              ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200'
              : isRejected
                ? 'bg-gradient-to-br from-red-50 to-orange-50 border-red-200'
                : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
        }`}
        data-testid="verification-card"
      >
        {/* Header */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm ${
              isVerified ? 'bg-green-100' : isPending ? 'bg-amber-100' : isRejected ? 'bg-red-100' : 'bg-blue-100'
            }`}>
              {isVerified ? (
                <BadgeCheck className="w-7 h-7 text-green-600" />
              ) : isPending ? (
                <Clock className="w-7 h-7 text-amber-600" />
              ) : isRejected ? (
                <AlertCircle className="w-7 h-7 text-red-600" />
              ) : (
                <ShieldCheck className="w-7 h-7 text-blue-600" />
              )}
            </div>
            
            <div className="flex-1">
              <h3 className="font-bold text-xl mb-1">
                {isVerified 
                  ? (i18n.language === 'fr' ? 'Profil Vérifié' : 'Verified Profile')
                  : isPending
                    ? (i18n.language === 'fr' ? 'Vérification en cours' : 'Verification in Progress')
                    : isRejected
                      ? (i18n.language === 'fr' ? 'Vérification refusée' : 'Verification Rejected')
                      : (i18n.language === 'fr' ? 'Faites-vous vérifier' : 'Get Verified')
                }
              </h3>
              
              <p className="text-gray-600 text-sm">
                {isVerified 
                  ? (i18n.language === 'fr' 
                      ? 'Votre identité est confirmée. Les voyageurs vous font confiance !' 
                      : 'Your identity is confirmed. Travelers trust you!')
                  : isPending
                    ? (i18n.language === 'fr' 
                        ? 'Notre équipe examine votre demande (24-48h)' 
                        : 'Our team is reviewing your request (24-48h)')
                    : isRejected
                      ? (i18n.language === 'fr' 
                          ? `Raison: ${user?.verification_rejection_reason || 'Document non conforme'}` 
                          : `Reason: ${user?.verification_rejection_reason || 'Non-compliant document'}`)
                      : (i18n.language === 'fr' 
                          ? 'Le badge vérifié augmente votre crédibilité et vos réservations' 
                          : 'The verified badge increases your credibility and bookings')
                }
              </p>
            </div>
          </div>

          {/* Benefits for non-verified */}
          {!isVerified && !isPending && (
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                { icon: CheckCircle, text: i18n.language === 'fr' ? '+40% de réservations' : '+40% bookings' },
                { icon: BadgeCheck, text: i18n.language === 'fr' ? 'Badge vérifié visible' : 'Visible verified badge' },
                { icon: Lock, text: i18n.language === 'fr' ? 'Données sécurisées' : 'Secure data' },
                { icon: ShieldCheck, text: i18n.language === 'fr' ? 'Confiance voyageurs' : 'Traveler trust' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                  <item.icon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Status indicators */}
          {isVerified && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              <BadgeCheck className="w-5 h-5" />
              {i18n.language === 'fr' ? 'Guide Vérifié' : 'Verified Guide'}
            </div>
          )}

          {isPending && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
              <Loader2 className="w-5 h-5 animate-spin" />
              {i18n.language === 'fr' ? 'En cours d\'examen...' : 'Under review...'}
            </div>
          )}

          {/* Action Button */}
          {!isVerified && !isPending && (
            <Button 
              onClick={() => setShowModal(true)}
              className="mt-6 w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              data-testid="start-verification-btn"
            >
              <ShieldCheck className="w-5 h-5 mr-2" />
              {isRejected 
                ? (i18n.language === 'fr' ? 'Réessayer la vérification' : 'Retry verification')
                : (i18n.language === 'fr' ? 'Démarrer la vérification' : 'Start verification')
              }
            </Button>
          )}
        </div>
      </div>

      {/* Verification Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" data-testid="verification-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-blue-500" />
              {i18n.language === 'fr' ? 'Vérification d\'identité' : 'Identity Verification'}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-6">
            {/* Document Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {i18n.language === 'fr' ? 'Type de document' : 'Document type'}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {documentTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setDocumentType(type.id)}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      documentType === type.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`doc-type-${type.id}`}
                  >
                    <type.icon className={`w-5 h-5 mx-auto mb-1 ${
                      documentType === type.id ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <span className={`text-xs ${
                      documentType === type.id ? 'text-blue-600 font-medium' : 'text-gray-600'
                    }`}>
                      {type.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Upload Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {i18n.language === 'fr' ? 'Photo du document' : 'Document photo'}
              </label>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="document-file-input"
              />

              {documentPreview ? (
                <div className="relative rounded-xl overflow-hidden border-2 border-blue-200">
                  <img 
                    src={documentPreview} 
                    alt="Document preview" 
                    className="w-full h-48 object-contain bg-gray-50"
                  />
                  <button
                    onClick={() => setDocumentPreview(null)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    data-testid="remove-document-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-48 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-3 hover:border-blue-400 hover:bg-blue-50/50 transition-all"
                  data-testid="upload-document-btn"
                >
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                    <Camera className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-gray-700">
                      {i18n.language === 'fr' ? 'Cliquez pour ajouter une photo' : 'Click to add a photo'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {i18n.language === 'fr' ? 'JPG, PNG - Max 10MB' : 'JPG, PNG - Max 10MB'}
                    </p>
                  </div>
                </button>
              )}
            </div>

            {/* Upload Progress */}
            {loading && uploadProgress > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {i18n.language === 'fr' ? 'Envoi en cours...' : 'Uploading...'}
                  </span>
                  <span className="font-medium text-blue-600">{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Security Notice */}
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <Lock className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-600">
                {i18n.language === 'fr' 
                  ? 'Vos données sont chiffrées et sécurisées. Nous ne partageons jamais vos documents avec des tiers.'
                  : 'Your data is encrypted and secure. We never share your documents with third parties.'}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowModal(false)} 
                className="flex-1"
                disabled={loading}
              >
                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleSubmitVerification}
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                disabled={loading || !documentPreview}
                data-testid="submit-verification-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {i18n.language === 'fr' ? 'Envoi...' : 'Sending...'}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {i18n.language === 'fr' ? 'Envoyer' : 'Submit'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GuideVerificationCard;
